import { useMutation, useQuery } from "@tanstack/react-query";
import Api from "../../../features/one-query/services/ApiServices";

interface videoGeneratorBody {
  prompt: string;
  connectionId: string;
  ratio: string;
  duration: string;
  style: string;
  resolution: string;
  seed?: number | null;
  model: string;
  version:string
}
interface ImageToVideoBody {
  prompt: string;
  connectionId: string;
  image?: File | string | null;
  ratio: string;
  duration: string;
  style: string;
  resolution: string;
  seed?: number | null;
  model: string;
  version: string;
  avatarId?: string;
  voiceId?: string;
}

export interface GetAvatarsRequestData{
  search?:string
}
export interface GetSoundEffectRequest{
  search?:string;
  categories?:number[] |null
}
///Get Video suite examples /////////////////////////////
const getVideoSuitSamples = async () => {
  const response = await Api.get(`/VideoSuite/Samples`);
  return response.data;
};

const userGetVideoSuiteSamples = () => {
  return useQuery({
    queryKey: ["GetVideoSuiteSamples"],
    queryFn: () => getVideoSuitSamples(),
  });
};

///Get Video suite Apps /////////////////////////////
const getVideoSuitApps = async () => {
  const response = await Api.get(`/VideoSuite/Apps`);
  return response.data;
};

const useGetVideoSuiteApps = () => {
  return useQuery({
    queryKey: ["GetVideoSuiteApps"],
    queryFn: () => getVideoSuitApps(),
  });
};
/// get video suite settings  /////////////////////////////////////////////////////
const getVideoSuitSettings = async (appId: string) => {
  const response = await Api.get(`/VideoSuite/Settings/${appId}`);
  return response.data;
};

const useGetVideoSuiteAppSettings = (appId: string) => {
  return useQuery({
    queryKey: ["GetVideoSuiteApps",appId],
    queryFn: () => getVideoSuitSettings(appId),
    enabled:!!appId
  });
};
/// API Video generator App /////////////////////////////////////////////////////
const videoGeneratorApp = async (
  videoGeneratorRequestBody: videoGeneratorBody
) => {
  const response = await Api.post(
    `/VideoSuite/TextToVideo`,
    videoGeneratorRequestBody
  );
  return response.data;
};

const useGenerateVideoGeneratorApp = () => {
  return useMutation({
    mutationKey: ["VideoGeneratorApp"],
    mutationFn: (videoGeneratorRequestBody: videoGeneratorBody) =>
      videoGeneratorApp(videoGeneratorRequestBody),
  });
};
/// API image to video App /////////////////////////////////////////////////////
const imageToVideoApp = async (ImageToVideoRequestBody: ImageToVideoBody) => {
  const formData = new FormData();
  formData.append("ConnectionId", ImageToVideoRequestBody.connectionId + "");
  formData.append("prompt", ImageToVideoRequestBody.prompt);
  if (ImageToVideoRequestBody.image)
    formData.append("image", ImageToVideoRequestBody.image);
  formData.append("ratio", ImageToVideoRequestBody.ratio + "");
  formData.append("model", ImageToVideoRequestBody.model + "");
  formData.append("version", ImageToVideoRequestBody.version + "");
  formData.append("duration", ImageToVideoRequestBody.duration + "");
  formData.append("style", ImageToVideoRequestBody.style + "");
  formData.append("resolution", ImageToVideoRequestBody.resolution + "");
  // formData.append("seed", ImageToVideoRequestBody.seed + "");

  const response = await Api.post(`/VideoSuite/ImageToVideo`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

const useGenerateImageToVideoApp = () => {
  return useMutation({
    mutationKey: ["ImageToVideoApp"],
    mutationFn: (ImageToVideoRequestBody: ImageToVideoBody) =>
      imageToVideoApp(ImageToVideoRequestBody),
  });
};
// =======================================

// Regenerate video
interface RegenerateDataRequest {
  historyId: string;
  connectionId: string;
  avatarId?: string;
  voiceId?: string;
}
const regenerateVideo = async (regenerateData: RegenerateDataRequest) => {
  try {
    const response = await Api.post("/VideoSuite/Regenerate", regenerateData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const useRegenerateVideo = () => {
  return useMutation({
    mutationKey: ["RegenerateVideo"],
    mutationFn: regenerateVideo,
  });
};

// =======================================

// Get video Avatars endpoint
const getAvatars = async (data: GetAvatarsRequestData) => {
  const params = new URLSearchParams();
  if (data?.search) params.append("Search", data.search);
  const response = await Api.get(`/VideoSuite/GetAvatars?${params.toString()}`);
  return response.data;
};

const useGetAvatars = (data?:GetAvatarsRequestData) => {
  return useQuery({
    queryKey: ["Avatars",data],
    queryFn: () => getAvatars(data!),
    enabled: !!data,
    refetchOnWindowFocus: false,
  });
};

// =======================================

// Get video Avatars voices endpoint
const getAvatarsVoices = async (data: GetAvatarsRequestData) => {
  const params = new URLSearchParams();
  if (data?.search) params.append("Search", data.search);
  const response = await Api.get(`/VideoSuite/GetVoices?${params.toString()}`);
  return response.data;
};

const useGetAvatarsVoices = (data?:GetAvatarsRequestData) => {
  return useQuery({
    queryKey: ["AvatarsVoices",data],
    queryFn: () =>  getAvatarsVoices(data!),
    enabled: !!data,
    refetchOnWindowFocus: false,
  });
};

// =======================================

// Generate avatar video endpoint.
const generateAvatarVideo = async (
  ImageToVideoRequestBody: ImageToVideoBody
) => {
  const response = await Api.post(
    `/VideoSuite/TextToVideoWithAvatar`,
    ImageToVideoRequestBody
  );
  return response.data;
};

const useGenerateAvatarVideo = () => {
  return useMutation({
    mutationKey: ["generateAvatarVideo"],
    mutationFn: (ImageToVideoRequestBody: ImageToVideoBody) =>
      generateAvatarVideo(ImageToVideoRequestBody),
  });
};

// =======================================

// Generate random script endpoint.
const generateRandomScript = async (connectionId: string) => {
  const response = await Api.post(
    `VideoSuite/GenerateRandomScript?connectionId=${connectionId}`
  );
  return response.data;
};

const useGenerateRandomScript = () => {
  return useMutation({
    mutationKey: ["generateAvatarVideo"],
    mutationFn: (connectionId: string) => generateRandomScript(connectionId),
  });
};

// ================================================================================

interface ImproveVideoPromptData {
  prompt: string;
  connectionId: string;
}
interface SoundEffectData {
  videoUrl:string;
  duration: string;
  soundEffectId: string;
}

//Improve video suite prompt endpoint.
const improveVideoSuitePrompt = async (data: ImproveVideoPromptData) => {
  const response = await Api.post(`/VideoSuite/EnhancePrompt`, data);
  return response.data;
};

const useImproveVideoSuitePrompt = () => {
  return useMutation({
    mutationKey: ["improveVideoSuitePrompt"],
    mutationFn: (data: ImproveVideoPromptData) => improveVideoSuitePrompt(data),
  });
};
////////////////////get All sound effects /////////////////////////////
const getAllSoundEffect = async (data:GetSoundEffectRequest) => {
   const params = new URLSearchParams();
  if (data?.search) params.append("Search", data.search);
  if (data?.categories) {
    data.categories.forEach((Category) => {
      params.append("Categories", Category.toString());
    });
  }
  const response = await Api.get(`SoundEffects?${params.toString()}`);
  return response.data;
};

const useGetAllSoundEffect = (data:GetSoundEffectRequest) => {
  return useQuery({
    queryKey: ["Get AllSoundEffect",data],
    queryFn:()=> getAllSoundEffect(data),
    refetchOnWindowFocus: false,

  });
};
////////////////////////////////////////////////////////////////////
const addSoundEffect = async (data: SoundEffectData) => {
  const response = await Api.post(`SoundEffects/ProcessSoundEffect`, data);
  return response.data;
};

const useAddSoundEffect = () => {
  return useMutation({
    mutationKey: ["addSoundEffect"],
    mutationFn: (data: SoundEffectData) => addSoundEffect(data),
  });
};
export {
  userGetVideoSuiteSamples,
  useGetVideoSuiteApps,
  useGetVideoSuiteAppSettings,
  useGenerateVideoGeneratorApp,
  useGenerateImageToVideoApp,
  useRegenerateVideo,
  useGetAvatars,
  useGetAvatarsVoices,
  useGenerateAvatarVideo,
  useGenerateRandomScript,
  useImproveVideoSuitePrompt,
  useGetAllSoundEffect,
  useAddSoundEffect,
};
